'use client';

import { Container } from '@eliadmin/core/src/components/ui/container';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import {
  activateSubscription,
  cancelSubscription,
  showSubscription,
  updateSubscription,
} from '@eliadmin/core/src/api-custom/updater-subscriptions-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import Link from '@eliadmin/core/src/components/link';

export default function ProfileSubscriptionPage() {
  const locale = useLocaleStore(s => s.currentLocale);
  const me = useCustomerStore(s => s.me);
  const queryClient = useQueryClient();
  const router = useRouter();
  const { t } = useTranslations();
  const { toast } = useToast();

  const { data } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => showSubscription({ locale, authToken: me?.authToken ?? '' }),
  });

  const subscription = data?.[0];

  const [email, setEmail] = useState(subscription?.email ?? '');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [subscriptionRulesAccepted, setSubscriptionRulesAccepted] = useState(false);

  const { mutateAsync: saveEmail } = useMutation({
    mutationFn: (nextEmail: string) =>
      updateSubscription({ locale, authToken: me?.authToken ?? '', body: { email: nextEmail } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  const { mutateAsync: activate } = useMutation({
    mutationFn: (body: { return_url: string }) =>
      activateSubscription({ locale, authToken: me?.authToken ?? '', body }),
    onSuccess: data => {
      router.push(data.url);
    },
    onError: (e: any) => {
      toast({
        title: t('text594'),
        variant: 'destructive',
      });
    },
  });

  const { mutateAsync: cancel } = useMutation({
    mutationFn: () => cancelSubscription({ locale, authToken: me?.authToken ?? '' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  const isActive = !!subscription?.is_active;

  const handleEmailBlur = async () => {
    if (!email || email === subscription?.email) return;
    await saveEmail(email);
  };

  // If subscription is already active, mark both checkboxes as accepted and lock them
  useEffect(() => {
    if (isActive) {
      setTosAccepted(true);
      setSubscriptionRulesAccepted(true);
    }
  }, [isActive]);

  return (
    <Container className="space-y-6 px-0">
      <h1 className="text-2xl font-semibold">{t('text1917')}</h1>

      <div className="space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2 text-sm">
            <div>
              {t('text3272')}: <strong>{subscription?.name ?? '---'}</strong>
            </div>
            <div>
              {t('text3273')}:{' '}
              <strong>
                {subscription?.price_without_vat
                  ? `${subscription.price_without_vat.toFixed(2)} €`
                  : '---'}
              </strong>
            </div>
            <div>
              {t('text3274')}: <strong>{subscription?.hours ?? '0.00'}</strong>
            </div>

            <div className="flex items-center gap-2">
              {t('text3275')}:
              <div
                className={
                  'flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ' +
                  (isActive
                    ? 'bg-success text-success-foreground'
                    : 'bg-destructive text-destructive-foreground')
                }
              >
                {isActive && <FiCheck />}
                {!isActive && <FiX />}
                {isActive ? t('text3276') : t('text3277')}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 text-right text-sm">
            {isActive && subscription?.date_stop && (
              <p>
                {t('text3278')}:{' '}
                <strong>{new Date(subscription.date_stop).toLocaleDateString()}</strong>
              </p>
            )}

            {isActive ? (
              <Button variant="default" size="sm" onClick={() => cancel()}>
                {t('text2270')}
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() =>
                  activate({
                    return_url: (process.env.NEXT_PUBLIC_APP_URL ?? '') + ROUTES.SUBSCRIPTION(),
                  })
                }
                disabled={!tosAccepted || !subscriptionRulesAccepted}
              >
                {t('text2099')}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3 text-sm">
          {/* TOS acceptance */}
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              checked={tosAccepted}
              required
              disabled={isActive}
              onCheckedChange={value => {
                if (isActive) return;
                setTosAccepted(!!value);
              }}
            />
            <span>
              {t('text264')}{' '}
              <Link
                href={'/vseobecne-obchodne-podmienky'}
                className="underline"
                prefetch={false}
                target="_blank"
              >
                {t('text265')}
              </Link>
            </span>
          </div>

          {/* Subscription rules acceptance */}
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              checked={subscriptionRulesAccepted}
              required
              disabled={isActive}
              onCheckedChange={value => {
                if (isActive) return;
                setSubscriptionRulesAccepted(!!value);
              }}
            />
            <span>
              {t('text264')}{' '}
              <Link href="/podmienky-predplatneho" className="underline" prefetch={false}>
                opakovanými platbami a podmienkami predplatného
              </Link>
            </span>
          </div>
        </div>
      </div>
    </Container>
  );
}
